import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useI18n } from "../i18n-context";
import { translations } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnersPage() {
  const { t, lang } = useI18n();
  const govs = translations[lang].coverage.governorates;

  const [data, setData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    governorate: "",
    business_type: "",
    daily_orders: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const u = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partners`, { ...data, locale: lang });
      toast.success(t.partners.form.success);
      setData({
        company_name: "",
        contact_person: "",
        email: "",
        phone: "",
        governorate: "",
        business_type: "",
        daily_orders: "",
        notes: "",
      });
    } catch {
      toast.error(t.partners.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader kicker={t.partners.kicker} title={t.partners.title} lead={t.partners.lead} />

      <section className="pb-24">
        <div className="container grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32 space-y-3">
            <div className="rounded-3xl bg-brand-ink text-white p-7">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-brand-orange mb-4">
                {lang === "ar" ? "ما الذي يحصل عليه شركاؤنا" : "What our partners get"}
              </div>
              <ul className="space-y-3">
                {[
                  lang === "ar" ? "مدير حساب مخصص" : "Dedicated account manager",
                  lang === "ar" ? "أسعار تنافسية حسب الحجم" : "Volume-based competitive pricing",
                  lang === "ar" ? "تكامل عبر API أو Excel" : "API or Excel integration",
                  lang === "ar" ? "تقارير مباشرة وتدقيق" : "Live reports & auditing",
                  lang === "ar" ? "تحقق OTP لكل تسليم" : "OTP verification on every delivery",
                  lang === "ar" ? "دعم 24/7 ميداني وتقني" : "24/7 field & technical support",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-brand-orange flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-brand-blue text-white p-7">
              <div className="text-xs font-mono uppercase tracking-[0.25em] opacity-80 mb-3">
                {lang === "ar" ? "زمن التوصيل" : "Turnaround"}
              </div>
              <div className="text-4xl font-display font-extrabold mb-2">&lt; 18h</div>
              <p className="text-sm text-white/80">
                {lang === "ar" ? "بدلاً من 72 ساعة في الخدمات التقليدية" : "vs. 72h in traditional services"}
              </p>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white text-brand-ink rounded-3xl p-8 border-2 border-brand-ink/5 shadow-sm space-y-5"
            data-testid="partners-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="field-label">{t.partners.form.company} *</label>
                <input type="text" required value={data.company_name} onChange={u("company_name")} className="field-input" data-testid="partner-company-input" />
              </div>
              <div>
                <label className="field-label">{t.partners.form.contact} *</label>
                <input type="text" required value={data.contact_person} onChange={u("contact_person")} className="field-input" data-testid="partner-contact-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="field-label">{t.partners.form.email} *</label>
                <input type="email" required value={data.email} onChange={u("email")} className="field-input" data-testid="partner-email-input" />
              </div>
              <div>
                <label className="field-label">{t.partners.form.phone} *</label>
                <input type="tel" required value={data.phone} onChange={u("phone")} className="field-input" data-testid="partner-phone-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="field-label">{t.partners.form.gov} *</label>
                <select required value={data.governorate} onChange={u("governorate")} className="field-input" data-testid="partner-gov-select">
                  <option value="">—</option>
                  {govs.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">{t.partners.form.daily}</label>
                <select value={data.daily_orders} onChange={u("daily_orders")} className="field-input" data-testid="partner-daily-select">
                  <option value="">—</option>
                  {t.partners.form.dailyOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="field-label">{t.partners.form.type}</label>
              <input type="text" value={data.business_type} onChange={u("business_type")} className="field-input" data-testid="partner-type-input" />
            </div>

            <div>
              <label className="field-label">{t.partners.form.notes}</label>
              <textarea rows={4} value={data.notes} onChange={u("notes")} className="field-input resize-none" data-testid="partner-notes-input" />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full !py-4 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="partner-submit-btn"
            >
              {loading ? t.partners.form.submitting : t.partners.form.submit}
              <ArrowRight className="h-4 w-4 flip-x" />
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
